import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CryptoJS from "crypto-js";
import { API_BASE_URL } from "../config/apiConfig";
import { ENCRYPTION_KEY } from "../config/key";
import { sendOtp, resetPassword, verifyOtp } from "../services/authService";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);


// Función para encriptar datos
const encryptData = (data) => {
  return CryptoJS.AES.encrypt(data, ENCRYPTION_KEY).toString();
};

// Función para desencriptar datos con validación
const decryptData = (encryptedData) => {
  try {
    if (!encryptedData) throw new Error("No hay datos encriptados");

    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    const decryptedData = bytes.toString(CryptoJS.enc.Utf8);

    if (!decryptedData) throw new Error("Datos desencriptados inválidos");

    return decryptedData;
  } catch (error) {
    return null; // Retorna null para evitar datos corruptos
  }
};



export const AuthProvider = ({ children }) => {
  const [userCorreo, setUserCorreo] = useState(() => {
    // Desencripta el correo almacenado en localStorage
    const encryptedCorreo = localStorage.getItem("encryptedCorreo");
    if (encryptedCorreo) {
      try {
        const bytes = CryptoJS.AES.decrypt(encryptedCorreo, ENCRYPTION_KEY);
        return bytes.toString(CryptoJS.enc.Utf8) || "";
      } catch (error) {
        return "";
      }
    }
    return "";
  });
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [passwordResetInProgress, setPasswordResetInProgress] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const inactivityTimeoutRef = useRef(null); // Referencia para el temporizador de inactividad
  const sessionTimeoutRef = useRef(null); // Referencia para el temporizador de 24 horas

  const INACTIVITY_LIMIT = 10800000; // 3 horas en milisegundos
  const SESSION_LIMIT = 28800000; // 24 horas en milisegundos

  // Función para cerrar sesión por tiempo de sesión
  const handleSessionTimeout = () => {
    setNotification({
      type: "error",
      message: "Sesión cerrada automáticamente después de 24 horas.",
    });
    logout();
  };

  // Función para reiniciar el temporizador de la sesión de 24 horas
  const resetSessionTimeout = () => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }
    if (token) {
      sessionTimeoutRef.current = setTimeout(() => {
        handleSessionTimeout();
      }, SESSION_LIMIT);
    }
  };

  // Función para reiniciar el temporizador de inactividad
  const resetInactivityTimeout = () => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    if (token) {
      inactivityTimeoutRef.current = setTimeout(() => {
        handleInactivity();
      }, INACTIVITY_LIMIT);
    }
  };

  // Manejo de inactividad
  const handleInactivity = () => {
    setNotification({ type: "error", message: "Sesión cerrada por inactividad." });
    logout();
  };

  // Configurar eventos de actividad
  useEffect(() => {
    const events = ["mousemove", "keydown", "scroll", "click"];
    const handleActivity = () => resetInactivityTimeout();

    events.forEach((event) => window.addEventListener(event, handleActivity));

    return () => {
      events.forEach((event) => window.removeEventListener(event, handleActivity));
    };
  }, [token]);

  // Cargar la sesión desde localStorage/sessionStorage
  useEffect(() => {
    const savedToken = localStorage.getItem("authToken");
    const savedUser = decryptData(localStorage.getItem("authUser"));
    // console.log(savedUser)
    const lastLoginTime = decryptData(localStorage.getItem("lastLoginTime"));

    if (savedToken && savedUser && lastLoginTime) {
      const currentTime = new Date().getTime();
      const elapsedTime = currentTime - parseInt(lastLoginTime, 10);

      if (elapsedTime >= SESSION_LIMIT) {
        // Si han pasado más de 24 horas, cerrar la sesión
        handleSessionTimeout();
      } else {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));

        // Restablecer el temporizador de la sesión restante
        const remainingTime = SESSION_LIMIT - elapsedTime;
        sessionTimeoutRef.current = setTimeout(() => {
          handleSessionTimeout();
        }, remainingTime);
      }
    }

    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/users/login`, credentials);
      const user = response.data.data;
      const session_token = response.data.data.session_token;

      setToken(session_token);
      setUser(user);

      const currentTime = new Date().getTime();
      localStorage.setItem("authToken", session_token);
      localStorage.setItem("authUser", encryptData(JSON.stringify(user)));
      localStorage.setItem("lastLoginTime", encryptData(currentTime.toString()));

      sessionStorage.setItem("authToken", session_token);

      document.cookie = `authToken=${session_token}; path=/; secure; samesite=strict; max-age=${SESSION_LIMIT / 1000}`;

      // Mostrar notificación de éxito
      setNotification({ type: "success", message: "Sesión iniciada correctamente." });

      // Reiniciar temporizadores
      resetInactivityTimeout();
      resetSessionTimeout();
    } catch (error) {
      setNotification({
        type: "error",
        message: error.response?.data?.message || "Error al iniciar sesión.",
      });
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);

    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
    localStorage.removeItem("lastLoginTime");
    sessionStorage.removeItem("authToken");

    document.cookie = "authToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC";

    if (inactivityTimeoutRef.current) clearTimeout(inactivityTimeoutRef.current);
    if (sessionTimeoutRef.current) clearTimeout(sessionTimeoutRef.current);

    navigate("/login");
  };

  const requestOtp = async (correo) => {
    try {
      setLoading(true);
      await sendOtp(correo);
      setUserCorreo(correo);

      // Encripta el correo y lo almacena en localStorage
      const encryptedCorreo = CryptoJS.AES.encrypt(correo, ENCRYPTION_KEY).toString();
      localStorage.setItem("encryptedCorreo", encryptedCorreo);
      
      setOtpSent(true);
      setPasswordResetInProgress(true); // Activa el proceso de restablecimiento
      setNotification({ type: "success", message: "Código OTP enviado al correo." });
      navigate("/verify-otp");
    } catch (error) {
      setNotification({
        type: "error",
        message: error.response?.data?.message || "Error al enviar el OTP.",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const verifyOtpCode = async (correo, otp) => {
    try {
      setLoading(true);
      await verifyOtp(correo, otp);
      setOtpVerified(true);
      setPasswordResetInProgress(true); // Activa el proceso de restablecimiento
      setNotification({ type: "success", message: "Código OTP verificado correctamente." });
      navigate("/reset-password"); // Redirige al formulario de restablecimiento de contraseña
    } catch (error) {
      setNotification({
        type: "error",
        message: error.response?.data?.message || "Código OTP incorrecto o expirado.",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (correo, newPassword) => {
    try {
      setLoading(true);
      await resetPassword(correo, newPassword); // Ajusta según la lógica del OTP
      setNotification({ type: "success", message: "Contraseña restablecida correctamente." });
      // Limpia el correo almacenado después de restablecer la contraseña
      localStorage.removeItem("encryptedCorreo");
      setOtpSent(false);
      setOtpVerified(false);
      setPasswordResetInProgress(false); // Finaliza el proceso al restablecer
      setUserCorreo("");
      navigate("/login");
    } catch (error) {
      setNotification({
        type: "error",
        message: error.response?.data?.message || "Error al restablecer la contraseña.",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const clearNotification = () => {
    setNotification(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        requestOtp,
        verifyOtpCode,
        handlePasswordReset,
        passwordResetInProgress,
        otpSent,
        otpVerified,
        userCorreo,
        loading,
        notification,
        clearNotification,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
